import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../../core/services/api.service';

interface CalendarEventRow {
  id: string;
  title: string;
  description?: string;
  eventType?: number;
  startTime: string;
  endTime: string;
  isAllDay?: boolean;
  location?: string;
  color?: string;
  taskId?: string | null;
  projectId?: string | null;
}

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule
  ],
  styles: [
    `
      .cal-page {
        padding: 1.25rem;
        display: grid;
        gap: 1rem;
      }
      .subtitle {
        opacity: 0.75;
        margin: 0.25rem 0 0;
      }
      .toolbar {
        display: flex;
        gap: 0.75rem;
        flex-wrap: wrap;
        align-items: center;
      }
      .error-banner {
        background: #fdecea;
        color: #b71c1c;
        padding: 0.75rem 1rem;
        border-radius: 8px;
      }
      .day-grid {
        display: grid;
        gap: 0.75rem;
      }
      .day-card h3 {
        margin: 0 0 0.5rem;
        font-size: 0.95rem;
      }
      .event {
        border-left: 4px solid #455a64;
        padding: 0.5rem 0.75rem;
        margin-bottom: 0.5rem;
        background: rgba(0, 0, 0, 0.03);
        border-radius: 0 8px 8px 0;
      }
      .event.task {
        background: rgba(21, 101, 192, 0.06);
      }
      .meta {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        font-size: 0.8rem;
        opacity: 0.8;
        margin-top: 0.25rem;
      }
      .empty {
        opacity: 0.65;
      }
    `
  ],
  template: `
<div class="cal-page">
  <div>
    <h1>Calendar</h1>
    <p class="subtitle">
      Task workflow updates appear here automatically when work moves between stages.
    </p>
  </div>

  @if (error()) {
    <div class="error-banner">{{ error() }}</div>
  }

  <div class="toolbar">
    <mat-form-field appearance="outline">
      <mat-label>From</mat-label>
      <input matInput type="date" [(ngModel)]="fromDate" (change)="load()" />
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>To</mat-label>
      <input matInput type="date" [(ngModel)]="toDate" (change)="load()" />
    </mat-form-field>
    <button mat-stroked-button type="button" (click)="load()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
    @if (loading()) {
      <mat-spinner diameter="28"></mat-spinner>
    }
  </div>

  <div class="day-grid">
    @for (day of days(); track day.key) {
      <mat-card class="day-card">
        <h3>{{ day.label }}</h3>
        @for (ev of day.events; track ev.id) {
          <div class="event" [class.task]="!!ev.taskId" [style.border-left-color]="ev.color || '#455a64'">
            <strong>{{ ev.title }}</strong>
            <div class="meta">
              <span>{{ formatTime(ev.startTime) }} – {{ formatTime(ev.endTime) }}</span>
              @if (ev.taskId) {
                <span class="chip">Task update</span>
              }
            </div>
            @if (ev.description) {
              <p>{{ ev.description }}</p>
            }
          </div>
        } @empty {
          <p class="empty">No events</p>
        }
      </mat-card>
    } @empty {
      <p class="empty">No calendar events in this range.</p>
    }
  </div>
</div>
  `
})
export class CalendarComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly days = signal<{ key: string; label: string; events: CalendarEventRow[] }[]>([]);

  fromDate = '';
  toDate = '';

  ngOnInit(): void {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 7);
    const end = new Date(today);
    end.setDate(end.getDate() + 21);
    this.fromDate = start.toISOString().slice(0, 10);
    this.toDate = end.toISOString().slice(0, 10);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const from = new Date(this.fromDate + 'T00:00:00Z').toISOString();
    const to = new Date(this.toDate + 'T23:59:59Z').toISOString();
    this.api.get<CalendarEventRow[]>('v1/calendar', { from, to } as never).subscribe({
      next: (items) => {
        const list = Array.isArray(items) ? items : [];
        const byDay = new Map<string, CalendarEventRow[]>();
        for (const ev of list) {
          const key = (ev.startTime || '').slice(0, 10);
          if (!byDay.has(key)) byDay.set(key, []);
          byDay.get(key)!.push(ev);
        }
        const days = [...byDay.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([key, events]) => ({
            key,
            label: new Date(key + 'T12:00:00').toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }),
            events
          }));
        this.days.set(days);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load calendar.');
      }
    });
  }

  formatTime(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
}
